/**
 * Typed Hono client factory + ApiError envelope helpers.
 *
 * `createApiClient(opts)` returns a fully-typed `hc<AppType>` client whose
 * `client.api.v1.*.$post()` / `$get()` calls have IntelliSense for params,
 * bodies, and response shapes. Web + extension wrap their tree in
 * `<ApiClientProvider>` (see `./context.tsx`) and consume hooks from
 * `./react`.
 *
 * Error mapping: every non-OK response from the API is decoded as an
 * `AppError` envelope per `plans/03-architecture/api.md` § Error model.
 * Hooks call `parseApiError(response)` to throw a typed `ApiError`.
 */

import { hc } from 'hono/client';

import type { AppType } from '@barel/api';

/* ─── Public types ──────────────────────────────────────────────────────── */

export interface ApiClientOptions {
  /** Base URL for the API (no trailing slash). */
  baseUrl: string;
  /**
   * Override fetch — handy for tests + non-browser envs (e.g. RN).
   * Defaults to the platform `fetch`.
   */
  fetch?: typeof fetch;
  /**
   * Mints an `X-Correlation-Id` header per request. Defaults to
   * `crypto.randomUUID()` when available; otherwise falls back to a
   * timestamp + random suffix.
   */
  mintCorrelationId?: () => string;
  /**
   * Optional global error sink — receives every `ApiError` as it is parsed.
   * Useful for telemetry breadcrumbs.
   */
  onError?: (err: ApiError) => void;
  /**
   * Extra headers merged into every request (e.g. `Authorization` for the
   * extension's bearer flow). Function form is re-evaluated per request.
   */
  headers?: Record<string, string> | (() => Record<string, string>);
  /**
   * Request credentials policy. Defaults to `'include'` so the web client's
   * cookie auth round-trips. The extension can override to `'omit'`.
   */
  credentials?: RequestCredentials;
}

export type ApiClient = ReturnType<typeof hc<AppType>>;

/**
 * Mirror of the `AppError` JSON envelope produced by the API.
 * Keep this in sync with `apps/api/src/middleware/error.ts`.
 */
export interface ApiErrorEnvelope {
  code: string;
  i18nKey?: string;
  i18nParams?: Record<string, string | number>;
  httpStatus: number;
  retryable?: boolean;
  details?: unknown;
  message?: string;
  correlationId?: string;
}

/**
 * Typed runtime error raised when the API returns a non-OK envelope.
 * Properties match the envelope so callers can render localized UIs via
 * `@barel/i18n` (`t(err.i18nKey, err.i18nParams)`).
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly httpStatus: number;
  public readonly i18nKey?: string;
  public readonly i18nParams?: Record<string, string | number>;
  public readonly retryable: boolean;
  public readonly details?: unknown;
  public readonly correlationId?: string;

  constructor(envelope: ApiErrorEnvelope) {
    super(envelope.message ?? envelope.code);
    this.name = 'ApiError';
    this.code = envelope.code;
    this.httpStatus = envelope.httpStatus;
    this.i18nKey = envelope.i18nKey;
    this.i18nParams = envelope.i18nParams;
    this.retryable = envelope.retryable ?? false;
    this.details = envelope.details;
    this.correlationId = envelope.correlationId;
  }

  /** Type guard — useful in caller `catch` blocks. */
  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }
}

/* ─── Internals ─────────────────────────────────────────────────────────── */

function defaultMintCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  // Fallback for older runtimes: timestamp + random suffix.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function resolveHeaders(headers: ApiClientOptions['headers']): Record<string, string> {
  if (!headers) return {};
  return typeof headers === 'function' ? headers() : headers;
}

/**
 * Decode an `AppError` envelope from a non-OK Response. Best-effort: if the
 * body isn't JSON or is malformed, falls back to a generic `internal_error`
 * envelope with the response's status code.
 */
export async function parseApiError(response: Response): Promise<ApiError> {
  const correlationId = response.headers.get('x-correlation-id') ?? undefined;
  let envelope: ApiErrorEnvelope;
  try {
    const data = (await response.clone().json()) as Partial<ApiErrorEnvelope>;
    envelope = {
      code: data.code ?? 'internal_error',
      httpStatus: data.httpStatus ?? response.status,
      i18nKey: data.i18nKey,
      i18nParams: data.i18nParams,
      retryable: data.retryable,
      details: data.details,
      message: data.message,
      correlationId: data.correlationId ?? correlationId,
    };
  } catch {
    envelope = {
      code: 'internal_error',
      httpStatus: response.status,
      message: response.statusText || `HTTP ${response.status}`,
      correlationId,
    };
  }
  return new ApiError(envelope);
}

/* ─── Factory ───────────────────────────────────────────────────────────── */

/**
 * Maps a typed-client instance to its base URL so non-typed call paths
 * (e.g. Better Auth catch-all) can resolve URLs without re-walking the hc
 * proxy. WeakMap → garbage-collected when the client is dropped.
 */
const clientBaseUrls = new WeakMap<object, string>();

/** Read the registered base URL for a client, or `''` if unknown. */
export function getClientBaseUrl(client: ApiClient): string {
  return clientBaseUrls.get(client as unknown as object) ?? '';
}

/**
 * Build a typed `hc<AppType>` client. Every request automatically receives
 * an `X-Correlation-Id` header; cookies ride along by default for the web
 * (`credentials: 'include'`).
 *
 * Consumers should NOT instantiate this themselves under normal flow — wrap
 * the React tree in `<ApiClientProvider options={...}>` and use
 * `useApiClient()` to read the singleton (memoized by base URL + fetch).
 */
export function createApiClient(opts: ApiClientOptions): ApiClient {
  const baseFetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  const mint = opts.mintCorrelationId ?? defaultMintCorrelationId;
  const credentials = opts.credentials ?? 'include';

  // Wrap fetch so we can:
  //  1. Inject `X-Correlation-Id` (per-request, unique) + caller-supplied
  //     headers.
  //  2. Apply credentials policy.
  //  3. Notify `onError` when a non-OK response surfaces (best-effort —
  //     parsing happens in the hook layer too).
  const wrappedFetch: typeof fetch = async (input, init) => {
    const userHeaders = resolveHeaders(opts.headers);
    const merged = new Headers(init?.headers);
    for (const [k, v] of Object.entries(userHeaders)) {
      if (!merged.has(k)) merged.set(k, v);
    }
    if (!merged.has('X-Correlation-Id')) {
      merged.set('X-Correlation-Id', mint());
    }
    const finalInit: RequestInit = {
      ...(init ?? {}),
      headers: merged,
      credentials,
    };
    const response = await baseFetch(input, finalInit);
    if (!response.ok && opts.onError) {
      // Fire-and-forget — the parse may fail silently if the body isn't JSON.
      void parseApiError(response)
        .then(opts.onError)
        .catch(() => {
          /* swallow */
        });
    }
    return response;
  };

  const client = hc<AppType>(opts.baseUrl, {
    fetch: wrappedFetch,
  });
  clientBaseUrls.set(client as unknown as object, opts.baseUrl);
  // hc<AppType> returns a Proxy that intercepts property *gets* but does
  // NOT trap arbitrary symbol-keyed *sets* — assignments silently no-op.
  // Stash the wrapped fetch in a side WeakMap so non-typed paths
  // (Better Auth catch-all in `react.ts`) can pull it out by client id.
  clientFetches.set(client as unknown as object, wrappedFetch);
  return client;
}

/** WeakMap: client → its wrapped fetch (cookies + correlation id baked in). */
const clientFetches = new WeakMap<object, typeof fetch>();

/** Read the wrapped fetch attached to a client (used for non-typed paths). */
export function getClientFetch(client: ApiClient): typeof fetch {
  return clientFetches.get(client as unknown as object) ?? globalThis.fetch.bind(globalThis);
}
